
import { useQuery } from '@tanstack/react-query';
import { computeAdjustedEstimateTotalAsync, AdjustmentMetadataForCalculation } from '@/utils/estimateTotalCalculation';

/**
 * Hook that calculates estimate total, total invoiced, and remaining balance.
 * Uses the centralized computeAdjustedEstimateTotal utility for consistency,
 * including markup removal filters and discount application.
 */
export const useInvoiceCalculations = (
  lineItems: any[],
  existingInvoices: any[],
  adjustmentMetadata?: AdjustmentMetadataForCalculation | null
) => {
  // Use react-query to handle the async base cost fetching
  const { data: adjustedTotal } = useQuery({
    queryKey: ['invoiceCalcAdjustedTotal', 
      lineItems?.map(i => i.id).join(',') || 'none',
      JSON.stringify(adjustmentMetadata || null)
    ],
    queryFn: async () => {
      if (!lineItems || lineItems.length === 0) return 0;
      const result = await computeAdjustedEstimateTotalAsync(lineItems, adjustmentMetadata);
      return result.adjustedSubtotal;
    },
    enabled: lineItems && lineItems.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const estimateTotal = adjustedTotal || 0;
  const totalInvoiced = existingInvoices?.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0) || 0;
  const remainingBalance = estimateTotal - totalInvoiced;

  return {
    estimateTotal,
    totalInvoiced,
    remainingBalance
  };
};
